import {
  createListAllQuery,
  getGoal,
  GetGoalQuery,
  GetGoalQueryVariables,
  goalByOwner,
  GoalByOwnerQueryVariables,
  Goal as GoalGraphql,
  graphqlCall,
  listGoals,
  ListGoalsQueryVariables,
} from '@akin-home/amplify';
import { Goal } from '@akin-home/goals';

import { getAuthorizedAPIOptions } from '@akin-home/auth/frontend';
import { objectToQueryParams } from '@akin-home/objects';
import { API } from 'aws-amplify';
import { useQuery, UseQueryOptions } from 'react-query';
import { goalGraphqlToModel } from '../functions';
import { goal_query_keys } from './query_keys';

export const listAllGoalsQuery = createListAllQuery<
  GoalGraphql,
  ListGoalsQueryVariables
>(listGoals, 'listGoals');

export const listGoalsByOwnerQuery = createListAllQuery<
  GoalGraphql,
  GoalByOwnerQueryVariables
>(goalByOwner, 'goalByOwner');

export type ListGoalsQueryParams = {
  query?: { name?: string };
};

/** New query to get all goals */
export const listAllGoals = async (params?: ListGoalsQueryParams) => {
  const result = await API.get(
    'GoalsAPI',
    `/goals?${objectToQueryParams(params?.query)}`,
    await getAuthorizedAPIOptions()
  );

  if (result.success) {
    return result.items;
  }
};

export const getGoalQuery = (params: GetGoalQueryVariables) =>
  graphqlCall<GetGoalQuery>(getGoal, params);

export const listUserGoalsByReferenceCode = async ({
  reference_code,
  user_id,
}: {
  reference_code: string;
  user_id: string;
}): Promise<Goal[]> => {
  const goals = await listAllGoalsQuery({
    filter: {
      participantIDS: { contains: user_id },
      reference_code: { contains: reference_code },
      isTemplate: { ne: true },
    },
  });

  return goals.map(goalGraphqlToModel);
};

export const useGetGoalQuery = ({ id }: { id?: string }, options?: any) =>
  useQuery(
    goal_query_keys.detail(id),
    async () => id && (await getGoalQuery({ id })).data.getGoal,
    {
      ...options,
      enabled: id !== undefined,
      select: (data) => goalGraphqlToModel(data as GoalGraphql),
    }
  );

export const useListGoalsQuery = (
  params?: ListGoalsQueryVariables,
  options?: UseQueryOptions<
    GoalGraphql[],
    unknown,
    Goal[],
    readonly [string, 'list', ListGoalsQueryVariables | undefined]
  >
) =>
  useQuery(
    goal_query_keys.list(params),
    async () => listAllGoalsQuery(params),
    {
      select(data) {
        return data ? data.map(goalGraphqlToModel) : [];
      },
      ...options,
    }
  );

export const useListOwnerGoalsQuery = (
  params?: GoalByOwnerQueryVariables,
  options?: UseQueryOptions<
    GoalGraphql[],
    unknown,
    Goal[],
    readonly [string, 'list', GoalByOwnerQueryVariables | undefined]
  >
) =>
  useQuery(
    goal_query_keys.list(params),
    async () => listGoalsByOwnerQuery(params),
    {
      select(data) {
        return data ? data.map(goalGraphqlToModel) : [];
      },
      ...options,
    }
  );

export const useListUsersGoals = ({
  username,
  user_id,
}: {
  username: string;
  user_id: string;
}) =>
  useListOwnerGoalsQuery({
    owner: username,
    filter: {
      participantIDS: { contains: user_id },
    },
  });

/** Calls the get goal query and formats to model */
export const useGetGoal = (id?: string, options?: any) => {
  const result = useGetGoalQuery({ id }, { ...options, enabled: !!id });

  return {
    ...result,
    goal: result.data as Goal | undefined,
  };
};
